/**
 * A text box raises the on-screen keyboard only when the viewer ASKS for it
 * (owner 2026-09-21).
 *
 * On a TV the focus ring lands on a field as the D-pad passes over it, and
 * Android answers that focus with the soft keyboard: it covers the page the
 * viewer was reading and has to be dismissed before they can carry on
 * navigating. So a field is INERT while it only holds focus — `readOnly` with
 * `inputmode="none"`, the pair every Android WebView honours — and becomes
 * editable when the viewer activates it:
 *
 *   - a tap or click on the field, or on a control in the same form (the search
 *     bar's ✕ clears the query and re-focuses the field; that is still a click
 *     on the box),
 *   - OK/Enter on a remote while the field has focus, which in the APK also asks
 *     the app to raise the keyboard (`window.sliqueKeyboard`) — the page cannot
 *     do that itself once the field already holds focus,
 *   - typing a character from a real keyboard, so a desktop or PWA user never
 *     has to click before the first keystroke.
 *
 * Losing focus re-arms the rule, so a field the viewer edited earlier does not
 * surprise them with a keyboard the next time the focus ring reaches it.
 *
 * `readOnly` blocks editing only — value assignment, `maxLength`, validation and
 * every existing `.focus()` call behave exactly as before, and nothing here
 * touches a field's events: the app's own handlers still run.
 */

const SKIP_TYPES = new Set([
  "checkbox",
  "radio",
  "range",
  "color",
  "file",
  "button",
  "submit",
  "reset",
  "image",
  "hidden",
]);

/** A pointer press counts as a click on the field for this long (ms). */
const CLICK_WINDOW_MS = 400;

/**
 * Is this `<input>` type a place to type — i.e. does the rule make it inert on
 * focus? Exported so the test that walks the app's own JSX can prove no field
 * was missed (and cannot be missed by a later one).
 */
export function takesTyping(type: string): boolean {
  return !SKIP_TYPES.has(type.toLowerCase());
}

type Field = HTMLInputElement | HTMLTextAreaElement;

interface FieldShape {
  tagName?: string;
  type?: string;
  disabled?: boolean;
  readOnly?: boolean;
  getAttribute(name: string): string | null;
  setAttribute(name: string, value: string): void;
  removeAttribute(name: string): void;
  closest?(selector: string): Element | null;
}

/**
 * Duck-typed rather than `instanceof HTMLInputElement`: the test suite's jsdom
 * exposes a document without publishing every element constructor as a global.
 */
function asField(target: EventTarget | null): Field | null {
  const el = target as FieldShape | null;
  if (!el || typeof el !== "object") return null;
  if (el.tagName !== "INPUT" && el.tagName !== "TEXTAREA") return null;
  if (el.disabled) return null;
  const type = String(el.type ?? "text").toLowerCase();
  if (el.tagName === "INPUT" && !takesTyping(type)) return null;
  return el as unknown as Field;
}

const activated = new WeakSet<object>();
const originalInputModes = new WeakMap<object, string | null>();

/**
 * The same set, in an order that can be read out.
 *
 * `activated` is a WeakSet, so it cannot be enumerated — and the device debug server
 * genuinely needs to know which fields the rule has made editable, because that
 * is the difference between "the D-pad opened this field" and "the ring merely
 * landed on it". Measured on the projector (2026-09-23) while chasing a keyboard
 * that would not come back: the answer WAS this set, and there was no way to read
 * it without inferring from `readOnly` and guessing. Bounded, because it only
 * exists to be looked at.
 */
const activatedOrder: Field[] = [];

function rememberActivated(field: Field): void {
  const at = activatedOrder.indexOf(field);
  if (at !== -1) activatedOrder.splice(at, 1);
  activatedOrder.push(field);
  if (activatedOrder.length > 20) activatedOrder.shift();
}

function forgetActivated(field: Field): void {
  const at = activatedOrder.indexOf(field);
  if (at !== -1) activatedOrder.splice(at, 1);
}

/**
 * The fields this rule has made editable, most recent last (the debug server's `/state`).
 * Exported for the debug bridge in `debug/deviceState.ts`, not for the app.
 */
export function editableFields(): (HTMLInputElement | HTMLTextAreaElement)[] {
  return activatedOrder.filter((field) => activated.has(field) && field.isConnected);
}

function makeInert(field: Field): void {
  if (!originalInputModes.has(field)) {
    originalInputModes.set(field, field.getAttribute("inputmode"));
  }
  field.setAttribute("inputmode", "none");
  field.readOnly = true;
}

function activate(field: Field): void {
  activated.add(field);
  rememberActivated(field);
  const original = originalInputModes.get(field) ?? null;
  if (original === null) field.removeAttribute("inputmode");
  else field.setAttribute("inputmode", original);
  field.readOnly = false;
  askDeviceForKeyboard();
}

/**
 * Tell the app to raise the keyboard, when there is an app to tell.
 *
 * The page CANNOT open it itself once the field already holds focus: Chromium
 * asks Android for the soft keyboard when focus lands on an editable element,
 * and clearing `readOnly` afterwards is not a new focus. Measured on the ADT-3
 * with the same armed field one press apart — OK (`DPAD_CENTER`) showed it
 * (viewport 480 -> 264) and ENTER, which is what a remote's OK actually
 * produces in the DOM, did not. Two page-level cures were tried on the device
 * and both failed: a guarded `blur()`/`focus()` from inside the trusted
 * keydown, and `field.click()` from inside it.
 *
 * So in the APK the page asks, and `SoftKeyboard` (bound as `window
 * .sliqueKeyboard`) calls `showSoftInput` for it. On the website and on any
 * build older than that channel the interface is simply absent and this is a
 * no-op — the browser's own focus handling is what raises the keyboard there.
 */
function askDeviceForKeyboard(): void {
  const device = (globalThis as { sliqueKeyboard?: { show?: () => unknown } }).sliqueKeyboard;
  if (!device || typeof device.show !== "function") return;
  try {
    device.show();
  } catch {
    // A keyboard request is never worth breaking the press that asked for it.
  }
}

/**
 * Install the rule on a document. Returns a function that removes it again
 * (tests use it; the app installs once and keeps it).
 */
export function installKeyboardOnDemand(doc: Document = document): () => void {
  // The last pointer press and the page region it landed in, so a click that
  // re-focuses a field from a sibling control (✕ clear, a filter chip) still
  // counts as asking for the keyboard.
  let pressedAt = 0;
  let pressedRegion: Element | null = null;

  function onFocusIn(event: Event): void {
    const field = asField(event.target);
    if (!field || activated.has(field)) return;
    const region = pressedRegion;
    const clickedHere =
      region !== null &&
      Date.now() - pressedAt <= CLICK_WINDOW_MS &&
      (region === (field as unknown as Element) ||
        region.contains(field as unknown as Node) ||
        (field.closest?.("form, [role=\"search\"]") ?? null) === region);
    if (clickedHere) activate(field);
    else makeInert(field);
  }

  function onFocusOut(event: Event): void {
    const field = asField(event.target);
    if (!field) return;
    activated.delete(field);
    forgetActivated(field);
  }

  function onPointerDown(event: Event): void {
    const target = event.target as FieldShape | null;
    pressedAt = Date.now();
    pressedRegion =
      (target?.closest?.("form, [role=\"search\"]") as Element | null) ??
      ((target as unknown as Element | null) ?? null);
    const field = asField(event.target);
    if (!field || activated.has(field)) return;
    // A field is normally focused by the browser's own handling of this press;
    // clear the block first so that focus is the one that raises the keyboard.
    activate(field);
  }

  function onKeyDown(event: KeyboardEvent): void {
    const field = asField(event.target);
    if (!field || activated.has(field)) return;
    if (event.key === "Enter") {
      // OK on a remote, or Enter on a keyboard: this press is what opens the
      // keyboard, so it must not also submit the form or run the app's handler.
      event.preventDefault();
      activate(field);
      return;
    }
    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      // Someone with a real keyboard is typing; let the character through.
      activate(field);
    }
  }

  doc.addEventListener("focusin", onFocusIn, true);
  doc.addEventListener("focus", onFocusIn, true);
  doc.addEventListener("focusout", onFocusOut, true);
  doc.addEventListener("blur", onFocusOut, true);
  doc.addEventListener("pointerdown", onPointerDown, true);
  doc.addEventListener("keydown", onKeyDown, true);

  return () => {
    doc.removeEventListener("focusin", onFocusIn, true);
    doc.removeEventListener("focus", onFocusIn, true);
    doc.removeEventListener("focusout", onFocusOut, true);
    doc.removeEventListener("blur", onFocusOut, true);
    doc.removeEventListener("pointerdown", onPointerDown, true);
    doc.removeEventListener("keydown", onKeyDown, true);
  };
}
